// src/hooks/useSchoolApiUrlSetup.ts
import { useState, useCallback } from 'react';
import { CENTRAL_API_URL } from '../../apiSetup/apiConfig';

export interface School {
  id: string;
  name: string;
  api_url: string;
}

export interface Option {
  label: string;
  value: string;
}

interface UseSchoolApiUrlSetupReturn {
  // Data
  states: Option[];
  districts: Option[];
  pincodes: Option[];
  schools: Option[];
  schoolData: School[];

  // Current selections
  selectedState: string;
  selectedDistrict: string;
  selectedPincode: string;
  selectedSchool: string;

  // Loading & error
  loading: boolean;
  error: string;

  // Actions
  handleStateChange: (value: string) => void;
  handleDistrictChange: (value: string) => void;
  handlePincodeChange: (value: string) => void;
  handleSchoolChange: (value: string) => void;
  fetchStates: () => Promise<void>;
  clearError: () => void;
  resetSelections: () => void;
}

const defaultOption = (label: string): Option => ({ label, value: '' });

export const useSchoolApiUrlSetup = (): UseSchoolApiUrlSetupReturn => {
  // Options
  const [states, setStates] = useState<Option[]>([defaultOption('Select a state')]);
  const [districts, setDistricts] = useState<Option[]>([defaultOption('Select a district')]);
  const [pincodes, setPincodes] = useState<Option[]>([defaultOption('Select a pincode')]);
  const [schools, setSchools] = useState<Option[]>([defaultOption('Select a school')]);
  const [schoolData, setSchoolData] = useState<School[]>([]);

  // Selections
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [selectedPincode, setSelectedPincode] = useState<string>('');
  const [selectedSchool, setSelectedSchool] = useState<string>('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const clearError = () => setError('');
  const resetSelections = () => {
    setSelectedDistrict('');
    setSelectedPincode('');
    setSelectedSchool('');
    setDistricts([defaultOption('Select a district')]);
    setPincodes([defaultOption('Select a pincode')]);
    setSchools([defaultOption('Select a school')]);
  };

  const fetchStates = async () => {
    setLoading(true);
    clearError();
    try {
      const res = await fetch(`${CENTRAL_API_URL}api/states`);
      const data = await res.json();
      if (data.status === 'success') {
        setStates([
          defaultOption('Select a state'),
          ...data.data.map((s: string) => ({ label: s, value: s })),
        ]);
      } else {
        setError(data.message || 'Failed to load states');
      }
    } catch (err) {
      setError('Network error while loading states');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistricts = async (state: string) => {
    setLoading(true);
    clearError();
    try {
      const res = await fetch(`${CENTRAL_API_URL}api/districts/${encodeURIComponent(state)}`);
      const data = await res.json();
      if (data.status === 'success') {
        setDistricts([
          defaultOption('Select a district'),
          ...data.data.map((d: string) => ({ label: d, value: d })),
        ]);
      } else {
        setError(data.message || 'Failed to load districts');
      }
    } catch (err) {
      setError('Network error while loading districts');
    } finally {
      setLoading(false);
    }
  };

  const fetchPincodes = async (state: string, district: string) => {
    setLoading(true);
    clearError();
    try {
      const res = await fetch(
        `${CENTRAL_API_URL}api/pincodes/${encodeURIComponent(state)}/${encodeURIComponent(district)}`
      );
      const data = await res.json();
      if (data.status === 'success') {
        setPincodes([
          defaultOption('Select a pincode'),
          ...data.data.map((p: string) => ({ label: p, value: p })),
        ]);
      } else {
        setError(data.message || 'Failed to load pincodes');
      }
    } catch (err) {
      setError('Network error while loading pincodes');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchools = async (state: string, district: string, pincode: string) => {
    setLoading(true);
    clearError();
    try {
      const res = await fetch(
        `${CENTRAL_API_URL}api/schools/${encodeURIComponent(state)}/${encodeURIComponent(district)}/${encodeURIComponent(pincode)}`
      );
      const data = await res.json();

      if (data.status === 'success') {
        const options: Option[] = [
          defaultOption('Select a school'),
          ...data.data.map((s: any) => ({
            label: s.name,
            value: s.id.toString(),
          })),
        ];
        setSchools(options);

        const normalized: School[] = data.data.map((s: any) => ({
          id: s.id.toString(),
          name: s.name,
          api_url: s.api_url,
        }));
        setSchoolData(normalized);
      } else {
        setError(data.message || 'Failed to load schools');
      }
    } catch (err) {
      setError('Network error while loading schools');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStateChange = useCallback((value: string) => {
    setSelectedState(value);
    resetSelections();
    clearError();
    if (value) fetchDistricts(value);
  }, []);

  const handleDistrictChange = useCallback((value: string) => {
    setSelectedDistrict(value);
    setSelectedPincode('');
    setSelectedSchool('');
    setPincodes([defaultOption('Select a pincode')]);
    setSchools([defaultOption('Select a school')]);
    clearError();
    if (value && selectedState) fetchPincodes(selectedState, value);
  }, [selectedState]);

  const handlePincodeChange = useCallback((value: string) => {
    setSelectedPincode(value);
    setSelectedSchool('');
    setSchools([defaultOption('Select a school')]);
    clearError();
    if (value && selectedState && selectedDistrict) {
      fetchSchools(selectedState, selectedDistrict, value);
    }
  }, [selectedState, selectedDistrict]);

  const handleSchoolChange = useCallback((value: string) => {
    setSelectedSchool(value);
    clearError();
  }, []);

  return {
    states,
    districts,
    pincodes,
    schools,
    schoolData,

    selectedState,
    selectedDistrict,
    selectedPincode,
    selectedSchool,

    loading,
    error,

    handleStateChange,
    handleDistrictChange,
    handlePincodeChange,
    handleSchoolChange,
    fetchStates,
    clearError,
    resetSelections,
  };
};